import React, { useState, KeyboardEvent, useRef } from 'react'
import './MessageInput.css'

const MAX_INPUT_LENGTH = 100
const VOICE_NOT_SUPPORTED_MESSAGE = 'Voice input is supported in Google Chrome.'

interface SpeechRecognitionAlternative {
  transcript: string
}

interface SpeechRecognitionResult {
  isFinal: boolean
  length: number
  [index: number]: SpeechRecognitionAlternative
}

interface SpeechRecognitionResultList {
  length: number
  [index: number]: SpeechRecognitionResult
}

interface SpeechRecognitionEvent {
  resultIndex: number
  results: SpeechRecognitionResultList
}

interface BrowserSpeechRecognition {
  lang: string
  continuous: boolean
  interimResults: boolean
  maxAlternatives: number
  start: () => void
  stop: () => void
  onresult: ((event: SpeechRecognitionEvent) => void) | null
  onerror: (() => void) | null
  onend: (() => void) | null
}

interface MessageInputProps {
  onSendMessage: (message: string) => void
  disabled?: boolean
}

export default function MessageInput({ onSendMessage, disabled }: MessageInputProps) {
  const [input, setInput] = useState('')
  const [isRecording, setIsRecording] = useState(false)
  const [voiceError, setVoiceError] = useState('')
  const recognitionRef = useRef<BrowserSpeechRecognition | null>(null)

  const getSpeechRecognition = () => {
    const SpeechRecognitionClass = (window as Window & {
      SpeechRecognition?: new () => BrowserSpeechRecognition
      webkitSpeechRecognition?: new () => BrowserSpeechRecognition
    }).SpeechRecognition || (window as Window & { webkitSpeechRecognition?: new () => BrowserSpeechRecognition }).webkitSpeechRecognition
    return SpeechRecognitionClass
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value
    // Limit to 100 characters
    if (value.length <= MAX_INPUT_LENGTH) {
      setInput(value)
    }
  }

  const handleSend = () => {
    if (input.trim() && !disabled && input.length <= MAX_INPUT_LENGTH) {
      // Truncate to 100 characters if somehow over limit
      const message = input.trim().substring(0, MAX_INPUT_LENGTH)
      onSendMessage(message)
      setInput('')
    }
  }

  const handleKeyPress = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const remainingChars = MAX_INPUT_LENGTH - input.length
  const isAtLimit = input.length >= MAX_INPUT_LENGTH

  const handleVoiceInput = () => {
    if (disabled) return

    if (isRecording) {
      recognitionRef.current?.stop()
      setIsRecording(false)
      return
    }

    const SpeechRecognitionClass = getSpeechRecognition()
    if (!SpeechRecognitionClass) {
      setVoiceError(VOICE_NOT_SUPPORTED_MESSAGE)
      return
    }

    setVoiceError('')
    const recognition = new SpeechRecognitionClass()
    recognitionRef.current = recognition
    recognition.lang = 'en-US'
    recognition.continuous = false
    recognition.interimResults = true
    recognition.maxAlternatives = 1

    const currentInput = input
    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let transcript = ''
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        transcript += event.results[i][0].transcript
      }
      const normalizedTranscript = transcript.trim().replace(/\s+/g, ' ')
      if (!normalizedTranscript) return
      const combinedText = `${currentInput} ${normalizedTranscript}`.trim().substring(0, MAX_INPUT_LENGTH)
      setInput(combinedText)
    }

    recognition.onerror = () => {
      setVoiceError('Could not capture voice. Please try again.')
      setIsRecording(false)
    }

    recognition.onend = () => {
      recognitionRef.current = null
      setIsRecording(false)
    }

    setIsRecording(true)
    recognition.start()
  }

  return (
    <div className="message-input-container">
      <div className="message-input-wrapper">
        <textarea
          value={input}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          placeholder={disabled ? 'Connecting...' : 'Type your message...'}
          disabled={disabled}
          rows={1}
          className="message-input"
          maxLength={MAX_INPUT_LENGTH}
        />
        <button
          onClick={handleVoiceInput}
          disabled={disabled}
          className={`voice-button ${isRecording ? 'voice-button-recording' : ''}`}
          title={isRecording ? 'Stop recording' : 'Start voice input'}
          aria-label={isRecording ? 'Stop voice recording' : 'Start voice recording'}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M12 15.5c2.209 0 4-1.791 4-4V7.5c0-2.209-1.791-4-4-4s-4 1.791-4 4v4c0 2.209 1.791 4 4 4Zm7-4a1 1 0 1 0-2 0 5 5 0 0 1-10 0 1 1 0 1 0-2 0 7.002 7.002 0 0 0 6 6.93V21H9a1 1 0 1 0 0 2h6a1 1 0 1 0 0-2h-2v-2.57A7.002 7.002 0 0 0 19 11.5Z"
              fill="currentColor"
            />
          </svg>
        </button>
        <button
          onClick={handleSend}
          disabled={disabled || !input.trim()}
          className="send-button"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M.5 1.163L1.619.522l13.5 7.5-13.5 7.5L.5 14.837V1.163z"
              fill="currentColor"
            />
          </svg>
        </button>
      </div>
      <div className="message-input-footer">
        {voiceError && <span className="voice-error">{voiceError}</span>}
        <span className={`char-counter ${isAtLimit ? 'char-counter-limit' : ''}`}>
          {remainingChars} / {MAX_INPUT_LENGTH}
        </span>
      </div>
    </div>
  )
}

